// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useEffect, useMemo, FC } from "react";

import { GroupedCombobox, Select, Text } from "@hightouch/htui";
import { v4 as uuidv4 } from "uuid";

import { useFormErrorContext } from "src/contexts/form-error-context";
import {
  ReferencePropertyDefaultOperators,
  isColumnReference,
  ReferencedPropertyCondition,
  ReferencePropertyOperatorOptions,
  filterSupportedOperators,
} from "src/types/visual";

import { FilterProps } from "./condition";
import { HStack } from "./shared";
import { XButton } from "./condition-buttons";
import { validateReferencePropertyFilter } from "./condition-validation";
import { Filter } from "./filter";
import { getColumnFromValue, getColumnOptions } from "./utils";
import { GroupedComboboxFilter } from "./combobox-filter";
import { useFilterableColumnsQuery } from "src/graphql";
import { isJsonPathColumn } from "@hightouch/lib/query/visual/types";

export type ReferencePropertyFilterProps =
  FilterProps<ReferencedPropertyCondition>;

export const ReferencePropertyFilter: FC<
  Readonly<ReferencePropertyFilterProps>
> = (props) => {
  const {
    modelId: eventModelId,
    columns: funnelConditionEventModelColumns, // These are passed in from the FunnelFilter
    condition,
    onUpdate,
    onRemove,
  } = props;
  const filterId = useMemo<string>(uuidv4, []);

  // Fetch columns of the selected event model
  const eventModelColumns = useFilterableColumnsQuery(
    {
      segmentId: eventModelId?.toString() ?? "",
    },
    {
      enabled: Boolean(eventModelId),
      select: (data) => data.segments_by_pk?.filterable_audience_columns ?? [],
    },
  );

  const { getErrors, setFieldError, removeErrors } = useFormErrorContext();

  const filterErrors = getErrors(filterId);
  const propertyError = filterErrors?.property;
  const valueFromColumnError = filterErrors?.valueFromColumn;

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    setFieldError(filterId, validateReferencePropertyFilter(condition));

    return () => {
      removeErrors([filterId]);
    };
  }, [condition.property, condition.valueFromColumn, filterId]);

  const propertyOptions = getColumnOptions(
    funnelConditionEventModelColumns ?? [],
    !isColumnReference(condition.property),
  );
  // Use string for valueFromColumn instead of column reference
  // TODO(samuel): show merged columns here too!
  const referenceOptions = getColumnOptions(eventModelColumns.data ?? [], true);
  const connectionType = props.audience?.connection?.type;
  const operatorOptions = condition.propertyType
    ? ReferencePropertyOperatorOptions[condition.propertyType].filter(
        filterSupportedOperators(
          connectionType,
          isJsonPathColumn(condition.property),
        ),
      )
    : undefined;
  const operatorLabel = operatorOptions?.find(
    (option) => option.value === condition.operator,
  )?.label;

  return (
    <HStack
      gap={2}
      sx={{ alignItems: "flex-start", input: { fontWeight: "medium" } }}
    >
      <GroupedComboboxFilter
        isLoading={props.isLoading || eventModelColumns.isLoading}
        isInvalid={Boolean(propertyError)}
        optionGroups={[
          {
            label: "Event properties",
            options: propertyOptions,
          },
        ].filter(({ options }) => options.length > 0)}
        placeholder="Select a property"
        value={condition.property}
        width="2xs"
        onChange={(value) => {
          const column = getColumnFromValue(propertyOptions, value);

          if (!value || !column) {
            return;
          }

          onUpdate((condition) => {
            condition.propertyType = column.type;
            condition.property = value;
            condition.operator = column.type
              ? ReferencePropertyDefaultOperators[column.type]
              : null;
            condition.valueFromColumn = null;
          });
        }}
      />
      {condition.property && (
        <Filter
          isEmpty={false} // Field is automatically populated
          content={
            <HStack
              gap={2}
              sx={{
                alignItems: "flex-start",
                button: { fontWeight: "medium" },
                input: { fontWeight: "medium" },
              }}
            >
              <Select
                removePortal
                options={operatorOptions ?? []}
                placeholder="Filter on"
                width="auto"
                value={condition.operator}
                onChange={(operator) =>
                  onUpdate((condition) => {
                    condition.operator = operator;
                  })
                }
              />
              <GroupedCombobox
                removePortal
                isInvalid={Boolean(valueFromColumnError)}
                isLoading={props.isLoading || eventModelColumns.isLoading}
                optionGroups={[
                  {
                    label: "Event properties",
                    options: referenceOptions,
                  },
                ].filter(({ options }) => options.length > 0)}
                placeholder="Select the initial event property"
                value={condition.valueFromColumn}
                width="2xs"
                onChange={(valueFromColumn) =>
                  onUpdate((condition) => {
                    condition.valueFromColumn = valueFromColumn ?? null;
                  })
                }
              />
            </HStack>
          }
          error={valueFromColumnError}
        >
          <Text color="text.secondary" fontWeight="medium">
            {operatorLabel}
          </Text>
          <Text
            isTruncated
            color={condition.valueFromColumn === null ? "text.secondary" : ""}
            fontWeight="medium"
          >
            {condition.valueFromColumn || "(Select the initial event property)"}
          </Text>
        </Filter>
      )}
      {onRemove && (
        <XButton isDisabled={props.isDisabled} onRemove={onRemove} />
      )}
    </HStack>
  );
};
